<script setup>
// Widget CDC de chamada WAVOIP (outbound). Mount global via `register.js` +
// `Dashboard.vue`; self-gate por `store.status !== 'idle'` no `<div>` raiz
// (D-04). Chrome espelha upstream `FloatingCallWidget.vue` (`bg-n-solid-2`,
// `rounded-xl`, `shadow-xl`, `outline outline-1 outline-n-strong`) com offset
// vertical `bottom-20` para stack acima do FloatingCallWidget (D-07 / D-08).
// Encerrar: `bg-n-ruby-9` + `i-ph-phone-x-bold`, sem confirm modal (D-09);
// dispara `endCall()` direto (D-20 — composable roteia internamente
// callOutgoing/callActive). Header: linha 1 = `displayName` (lookup reativo
// Vuex via `useMapGetter('contacts/getContact')`, fallback `phone || '—'` —
// D-16 / D-18), linha 2 = mm:ss ou "Ligando para X…" (D-10). Ícone WhatsApp
// `i-ph-whatsapp-logo` em `text-n-teal-9` no slot do avatar (D-11). Timer
// inline + computed mm:ss (D-13 — desvio explícito do enunciado original
// de WIDGET-03; D-14); arranca no mount, limpa no unmount (Pitfall 2). Local
// `elapsed` é a fonte de verdade — drift sub-segundo aceito (D-15). Sem
// controles extras além de Encerrar (D-21 / WIDGET-08). Sem `<Transition>`
// (D-12). Tailwind only — CLAUDE.md.
// Origem: ADR 0007.
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useWavoipCallsStore } from '../stores/wavoipCalls';
import { useWavoipCallSession } from '../composables/useWavoipCallSession';

const { t } = useI18n();
const store = useWavoipCallsStore();
const { endCall } = useWavoipCallSession();

const getContact = useMapGetter('contacts/getContact');
const contact = computed(() => getContact.value(store.contactId));
const displayName = computed(() => contact.value?.name || store.phone || '—');
// WR-01 — bind through computed para garantir string (i18n placeholder
// nunca recebe `null` se uma mutação futura do store flipar `status` antes
// de popular `phone`).
const dialingLabel = computed(() =>
  t('CDC.WAVOIP.widget.dialing', { phone: store.phone || '' })
);

// D-13 / D-14 — timer inline + computed mm:ss. CR-01/CR-02: o widget é
// mountado uma única vez (Dashboard.vue sem `v-if` no consumer; `v-if`
// interno só alterna DOM, não destrói instância). Dirigir o setInterval
// pelo `watch(store.status)` em vez de onMounted/onUnmounted garante (1)
// reset de `elapsed` na entrada de `dialing` -- timer conta desde dialing,
// NÃO desde peerAccept (D-15) -- e (2) que o tick só roda enquanto há uma
// chamada ativa, evitando wake-up do event loop a cada segundo durante
// toda a sessão idle. O onUnmounted permanece como defense-in-depth para
// teardown legítimo do widget (logout).
const elapsed = ref(0);
let timerId = null;
watch(
  () => store.status,
  status => {
    clearInterval(timerId);
    timerId = null;
    if (status === 'dialing') {
      elapsed.value = 0;
    }
    if (status === 'dialing' || status === 'in_call') {
      timerId = setInterval(() => {
        elapsed.value += 1;
      }, 1000);
    }
  },
  { immediate: true }
);
onUnmounted(() => clearInterval(timerId));

const formatted = computed(() => {
  const m = Math.floor(elapsed.value / 60);
  const s = elapsed.value % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
});

// WR-03 — accessibility label dinâmico: screen readers anunciam o estado
// atual da widget (dialing / in_call) via `role="status"` + `aria-live`
// no root. O `aria-label` complementa para AT que não lê o subtree do
// status region.
const ariaLabel = computed(() => {
  if (store.status === 'in_call') {
    return t('CDC.WAVOIP.widget.in_call', { timer: formatted.value });
  }
  if (store.status === 'dialing') {
    return dialingLabel.value;
  }
  return '';
});
</script>

<template>
  <div
    v-if="store.status !== 'idle'"
    role="status"
    aria-live="polite"
    :aria-label="ariaLabel"
    class="fixed bottom-20 ltr:right-4 rtl:left-4 z-50 w-72 flex items-center gap-3 p-4 bg-n-solid-2 rounded-xl shadow-xl outline outline-1 outline-n-strong"
  >
    <i class="text-2xl i-ph-whatsapp-logo text-n-teal-9 shrink-0" />
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium text-n-slate-12 truncate mb-0">
        {{ displayName }}
      </p>
      <p
        v-if="store.status === 'in_call'"
        class="font-mono text-sm text-n-teal-9"
      >
        {{ formatted }}
      </p>
      <p v-else-if="store.status === 'dialing'" class="text-xs text-n-slate-11">
        {{ dialingLabel }}
      </p>
    </div>
    <button
      :aria-label="t('CDC.WAVOIP.widget.end_button')"
      class="flex justify-center items-center w-10 h-10 bg-n-ruby-9 hover:bg-n-ruby-10 rounded-full transition-colors shrink-0"
      @click="endCall"
    >
      <i class="text-lg text-white i-ph-phone-x-bold" />
    </button>
  </div>
</template>
